import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, CreditCard, Banknote, ShieldAlert, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { paymentsAPI } from '../services/PaymentApi';
import useStripeCheckout from '../services/PaymentStripeHooks';

/**
 * The way out of a card payment that never finished.
 *
 * A row reading "Credit / debit card · Unpaid" hides two completely different
 * situations, and offering the same buttons for both is how someone gets
 * charged twice. So nothing is offered until Stripe has been asked:
 *
 *   nothing was charged  → pay again, or stop using a card and hand over cash
 *   something was charged → that is their money, and it needs a person
 *
 * The check is the first thing this does and the only thing it does until it
 * comes back. Everything below branches off its answer rather than off what the
 * payer thinks happened — most people who are sure they were charged are
 * remembering a different payment, and the ones who are right deserve better
 * than a form.
 */

const OUTCOME = {
    settled: {
        tone: 'good',
        Icon: CheckCircle2,
        title: 'Stripe had this payment after all',
        body: 'It was paid and we had not recorded it. This is now marked paid — nothing further is owed.',
    },
    already_paid: {
        tone: 'good',
        Icon: CheckCircle2,
        title: 'Already paid',
        body: 'Nothing is owed on this registration.',
    },
    no_session: {
        tone: 'plain',
        Icon: CreditCard,
        title: 'No card payment was ever started',
        body: 'Nothing reached Stripe, so no money can have left an account through us.',
    },
    unpaid: {
        tone: 'plain',
        Icon: CreditCard,
        title: 'Stripe has the checkout, and it was never paid',
        body: 'The checkout was opened and abandoned. No money should have left the account.',
    },
    review: {
        tone: 'warn',
        Icon: AlertTriangle,
        title: 'This one needs a person',
        body: "Stripe's answer does not settle it. It has been flagged for review — "
            + 'nothing will be charged again until someone has looked.',
    },
};

const TONE = {
    good:  'bg-green-50 border-green-200 text-green-800',
    warn:  'bg-amber-50 border-amber-200 text-amber-800',
    plain: 'bg-blue-50 border-blue-200 text-blue-800',
};

/**
 * @param {'program'|'tournament'} kind
 * @param {number} registrationId
 * @param {boolean} canPayByCard  is the viewer the person whose card would be charged
 * @param {string} returnPath     where Stripe should send them back to
 * @param {() => void} onResolved called after anything that changes the row
 * @param {() => void} onClose
 */
export default function UnpaidPaymentActions({
    kind,
    registrationId,
    canPayByCard,
    returnPath,
    onResolved,
    onClose,
}) {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const { startCheckout, redirecting } = useStripeCheckout();

    const [checking, setChecking]   = useState(false);
    const [outcome, setOutcome]     = useState(null);
    const [busy, setBusy]           = useState(null);
    const [disputing, setDisputing] = useState(false);
    const [message, setMessage]     = useState('');

    const runCheck = async () => {
        setChecking(true);
        try {
            const { results } = await paymentsAPI.checkRegistrations({ kind, registrationIds: [registrationId] });
            const result = results?.[0];
            if (!result) throw new Error('The check came back empty');
            setOutcome(result.outcome);
            // Settled means the row on screen is now wrong — it says unpaid and
            // the money is in.
            if (result.outcome === 'settled' || result.outcome === 'already_paid') onResolved?.();
        } catch (err) {
            toast.error(err?.message || 'Could not check this payment with Stripe');
            onClose?.();
        } finally {
            setChecking(false);
        }
    };

    // Asked once, on open. Offering options before the answer is the whole bug.
    React.useEffect(() => { runCheck(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const handleSwitch = async (paymentMethod) => {
        setBusy(paymentMethod);
        try {
            await paymentsAPI.switchPaymentMethod({ kind, registrationId, paymentMethod });
            toast.success(`Switched to ${paymentMethod}. It will show as paid once the coach confirms receipt.`);
            onResolved?.();
            onClose?.();
        } catch (err) {
            toast.error(err?.message || 'Could not change the payment method');
        } finally {
            setBusy(null);
        }
    };

    const handleDispute = async () => {
        setBusy('dispute');
        try {
            const res = await paymentsAPI.raisePaymentDispute({ kind, registrationId, message });
            toast.success(res?.message || 'Dispute raised');
            onResolved?.();
            onClose?.();
        } catch (err) {
            toast.error(err?.message || 'Could not raise the dispute');
        } finally {
            setBusy(null);
        }
    };

    if (checking) {
        return (
            <div className={`px-4 pb-3 flex items-center gap-2 text-xs ${theme.textMuted}`}>
                <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500" />
                Checking this payment with Stripe…
            </div>
        );
    }

    const info = OUTCOME[outcome];
    if (!info) return null;

    const { Icon } = info;
    // Retrying is only ever offered when Stripe has said there is nothing
    // outstanding. Anything unresolved goes to a human instead.
    const canRetry = outcome === 'unpaid' || outcome === 'no_session';
    const done     = outcome === 'settled' || outcome === 'already_paid';

    return (
        <div className="px-4 pb-3">
            <div className={`rounded-lg border p-3 ${TONE[info.tone]}`}>
                <div className="flex items-start gap-2">
                    <Icon size={15} className="mt-0.5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold">{info.title}</p>
                        <p className="text-[11px] mt-0.5 leading-relaxed opacity-90">{info.body}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-0.5 rounded hover:bg-black/5 transition flex-shrink-0"
                        title="Close"
                    >
                        <X size={13} />
                    </button>
                </div>

                {!done && !disputing && (
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                        {canRetry && canPayByCard && (
                            <button
                                // The checkout PAGE rather than straight to
                                // Stripe: the payer gets a total, a choice of
                                // method and somewhere to land if the card
                                // fails — the same checkout everything else in
                                // the app now uses.
                                onClick={() => navigate(`/checkout?kind=${encodeURIComponent(kind)}`
                                    + `&ids=${encodeURIComponent(registrationId)}`
                                    + `&from=${encodeURIComponent(returnPath)}`
                                    + `&title=${encodeURIComponent(KIND_LABEL[kind] || 'Payment')}`)}
                                disabled={redirecting}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-semibold ${theme.buttonPrimary} disabled:opacity-50 transition`}
                            >
                                <CreditCard size={12} />
                                {redirecting ? 'Opening…' : 'Pay now'}
                            </button>
                        )}
                        {canRetry && (
                            <>
                                <button
                                    onClick={() => handleSwitch('cash')}
                                    disabled={!!busy}
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-semibold ${theme.buttonSecondary} disabled:opacity-50 transition`}
                                >
                                    <Banknote size={12} />
                                    {busy === 'cash' ? 'Switching…' : 'Switch to cash'}
                                </button>
                                <button
                                    onClick={() => handleSwitch('venmo')}
                                    disabled={!!busy}
                                    className={`px-2.5 py-1 rounded text-[11px] font-semibold ${theme.buttonSecondary} disabled:opacity-50 transition`}
                                >
                                    {busy === 'venmo' ? 'Switching…' : 'Switch to Venmo'}
                                </button>
                            </>
                        )}
                        <button
                            onClick={() => setDisputing(true)}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-semibold ${theme.buttonDanger} transition`}
                        >
                            <ShieldAlert size={12} />
                            Money was taken — raise a dispute
                        </button>
                    </div>
                )}

                {!done && disputing && (
                    <div className="mt-3">
                        <p className="text-[11px] mb-1.5 leading-relaxed">
                            Tell us what you saw — the amount, the date, the card, and where you saw it
                            leave. Kynaix and the organiser both get this.
                        </p>
                        <textarea
                            value={message}
                            onChange={e => setMessage(e.target.value)}
                            rows={3}
                            maxLength={4000}
                            placeholder="e.g. $180 left my Visa ending 4242 on 2 Aug, it shows on my bank statement as KYNAIX SPORTS."
                            className={`w-full px-2.5 py-2 text-xs rounded-lg border ${theme.border} ${theme.input} outline-none focus:ring-2 focus:ring-pink-500`}
                        />
                        <div className="flex items-center gap-2 mt-2">
                            <button
                                onClick={handleDispute}
                                disabled={busy === 'dispute'}
                                className={`px-2.5 py-1 rounded text-[11px] font-semibold ${theme.buttonDanger} disabled:opacity-50 transition`}
                            >
                                {busy === 'dispute' ? 'Raising…' : 'Raise dispute'}
                            </button>
                            <button
                                onClick={() => setDisputing(false)}
                                className={`px-2.5 py-1 rounded text-[11px] font-semibold border ${theme.border} ${theme.text} ${theme.cardHover} transition`}
                            >
                                Back
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
